import { DarkMode, DeviceUnknown, LightMode, Refresh, Settings } from '@mui/icons-material';
import {
    Box,
    Button,
    Divider,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { defaultColors } from '@sharedInterfaces/defaultColors';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { IStyleDTO } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { setCompanyStyle } from '@store/reducer/companyReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { EDarkMode, useAppTheme } from '../../../Theme/ThemeContext';

export interface ThemeEditorProps {
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    className?: string;
    showSettingsLink?: boolean;
}

export default function ThemeEditor({
    size = 'medium',
    fullWidth = false,
    showSettingsLink = false,
}: ThemeEditorProps) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const style = useSelector((state: AppState) => state.company.style);

    const {
        isDarkMode,
        isDefaultTheme,
        changeDarkMode,
        saveStyle,
        setPrimaryColor,
        setSecondaryColor,
        setOnPrimaryColor,
        setOnSecondaryColor,
    } = useAppTheme();

    const {
        primary: primaryColor,
        onPrimary: onPrimaryColor,
        secondary: secondaryColor,
        onSecondary: onSecondaryColor,
    } = style;
    // Theme mode state: 0 = light, 1 = system, 2 = dark
    const [themeMode, setThemeMode] = useState<0 | 1 | 2>(isDarkMode ? 2 : 0);

    const [tempPrimaryColor, setTempPrimaryColor] = useState(primaryColor);
    const [tempSecondaryColor, setTempSecondaryColor] = useState(secondaryColor);
    const [tempOnPrimaryColor, setTempOnPrimaryColor] = useState(onPrimaryColor);
    const [tempOnSecondaryColor, setTempOnSecondaryColor] = useState(onSecondaryColor);
    const [changed, setChanged] = useState(false);
    const [saving, setSaving] = useState(false);
    const [originalStyle, setOriginalStyle] = useState(style);

    // Determine what to show based on size
    const showTextInputs = size !== 'small';
    const showAllColors = size === 'large';

    // Update local state when theme context changes
    useEffect(() => {
        setTempPrimaryColor(primaryColor);
        setTempSecondaryColor(secondaryColor);
        setTempOnPrimaryColor(onPrimaryColor);
        setTempOnSecondaryColor(onSecondaryColor);
    }, [primaryColor, secondaryColor, onPrimaryColor, onSecondaryColor]);

    // Color validation helper
    const isValidColor = (color: string): boolean => {
        const colorRegex =
            /^#([0-9A-F]{3}){1,2}$|^rgb\(\d+,\s*\d+,\s*\d+\)$|^rgba\(\d+,\s*\d+,\s*\d+,\s*[0-1]?(\.\d+)?\)$|^hsl\(\d+,\s*\d+%,\s*\d+%\)$|^hsla\(\d+,\s*\d+%,\s*\d+%,\s*[0-1]?(\.\d+)?\)$/i;
        return colorRegex.test(color.trim());
    };

    // Live preview effect - update the theme in real-time
    useEffect(() => {
        // Only update if colors are valid
        if (
            isValidColor(tempPrimaryColor) &&
            isValidColor(tempSecondaryColor) &&
            isValidColor(tempOnPrimaryColor) &&
            isValidColor(tempOnSecondaryColor)
        ) {
            setPrimaryColor(tempPrimaryColor);
            setSecondaryColor(tempSecondaryColor);
            setOnPrimaryColor(tempOnPrimaryColor);
            setOnSecondaryColor(tempOnSecondaryColor);

            // Update Redux store for global preview
            dispatch(
                setCompanyStyle({
                    primary: tempPrimaryColor,
                    onPrimary: tempOnPrimaryColor,
                    secondary: tempSecondaryColor,
                    onSecondary: tempOnSecondaryColor,
                    logoIdLight: style.logoIdLight,
                    logoIdDark: style.logoIdDark,
                }),
            );
        }

        // Check if values have changed from original
        if (
            tempPrimaryColor !== originalStyle.primary ||
            tempSecondaryColor !== originalStyle.secondary ||
            tempOnPrimaryColor !== originalStyle.onPrimary ||
            tempOnSecondaryColor !== originalStyle.onSecondary
        ) {
            setChanged(true);
        } else {
            setChanged(false);
        }

        // Cleanup function to restore original colors if not saved
        return () => {
            if (changed && !saving) {
                dispatch(setCompanyStyle(originalStyle));
                setPrimaryColor(originalStyle.primary);
                setSecondaryColor(originalStyle.secondary);
                setOnPrimaryColor(originalStyle.onPrimary);
                setOnSecondaryColor(originalStyle.onSecondary);
            }
        };
    }, [
        tempPrimaryColor,
        tempSecondaryColor,
        tempOnPrimaryColor,
        tempOnSecondaryColor,
        dispatch,
        originalStyle,
        setPrimaryColor,
        setSecondaryColor,
        setOnPrimaryColor,
        setOnSecondaryColor,
        changed,
        saving,
    ]);

    const reset = () => {
        setTempPrimaryColor(defaultColors.light.primary);
        setTempSecondaryColor(defaultColors.light.secondary);
        setTempOnPrimaryColor(defaultColors.light.onPrimary);
        setTempOnSecondaryColor(defaultColors.light.onSecondary);
        setChanged(true);
    };

    const handlePrimaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempPrimaryColor(event.target.value);
        setChanged(true);
    };

    const handleSecondaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempSecondaryColor(event.target.value);
        setChanged(true);
    };

    const handleOnPrimaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempOnPrimaryColor(event.target.value);
        setChanged(true);
    };

    const handleOnSecondaryColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempOnSecondaryColor(event.target.value);
        setChanged(true);
    };

    const applyColorChanges = () => {
        if (!changed) return;
        setSaving(true);

        const newStyle: IStyleDTO = {
            primary: tempPrimaryColor,
            secondary: tempSecondaryColor,
            onPrimary: tempOnPrimaryColor,
            onSecondary: tempOnSecondaryColor,
            logoIdLight: style.logoIdLight,
            logoIdDark: style.logoIdDark,
        };

        saveStyle(newStyle)
            .then(() => {
                setChanged(false);
                setOriginalStyle(newStyle);
            })
            .catch((error) => {
                showErrorDialog(error.toString());
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <Box
            sx={{
                p: 2,
                width: fullWidth && !isMobile ? '100%' : 300,
                bgcolor: 'background.paper',
                display: fullWidth && !isMobile ? 'flex' : 'block',
                flexWrap: 'wrap',
                gap: 2,
            }}
        >
            <Box sx={{ width: fullWidth && !isMobile ? '30%' : '100%' }}>
                <Typography variant="h6" gutterBottom>
                    {langStrings.coloringTitle}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Theme Mode:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ToggleButtonGroup
                            value={themeMode}
                            exclusive
                            onChange={(_, newMode) => {
                                if (newMode !== null) {
                                    setThemeMode(newMode);
                                    if (newMode === 2) {
                                        // Dark mode
                                        if (!isDarkMode) changeDarkMode(EDarkMode.DARK);
                                    } else if (newMode === 0) {
                                        // Light mode
                                        if (isDarkMode) changeDarkMode(EDarkMode.LIGHT);
                                    } else {
                                        changeDarkMode(EDarkMode.DEFAULT);
                                    }
                                }
                            }}
                            size="small"
                            sx={{ width: '100%' }}
                        >
                            <ToggleButton value={0} aria-label="light mode" sx={{ flex: 1 }}>
                                <LightMode fontSize="small" sx={{ mr: 0.5 }} />
                                Light
                            </ToggleButton>
                            <ToggleButton value={1} aria-label="system mode" sx={{ flex: 1 }}>
                                <DeviceUnknown fontSize="small" sx={{ mr: 0.5 }} />
                                System
                            </ToggleButton>
                            <ToggleButton value={2} aria-label="dark mode" sx={{ flex: 1 }}>
                                <DarkMode fontSize="small" sx={{ mr: 0.5 }} />
                                Dark
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </Box>

            {size !== 'small' && (
                <Box
                    sx={{
                        mt: isMobile || !fullWidth ? 2 : 0,
                        width: fullWidth && !isMobile ? '40%' : '100%',
                    }}
                >
                    <Typography variant="subtitle2" gutterBottom>
                        {langStrings.primaryColor}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TextField
                            type="color"
                            value={tempPrimaryColor}
                            onChange={handlePrimaryColorChange}
                            size="small"
                            disabled={saving}
                            sx={{ width: 70 }}
                        />
                        {showTextInputs && (
                            <TextField
                                value={tempPrimaryColor}
                                onChange={handlePrimaryColorChange}
                                size="small"
                                placeholder={defaultColors.light.primary}
                                disabled={saving}
                                sx={{ flexGrow: 1 }}
                            />
                        )}
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                        {langStrings.secondaryColor}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TextField
                            type="color"
                            value={tempSecondaryColor}
                            onChange={handleSecondaryColorChange}
                            size="small"
                            disabled={saving}
                            sx={{ width: 70 }}
                        />
                        {showTextInputs && (
                            <TextField
                                value={tempSecondaryColor}
                                onChange={handleSecondaryColorChange}
                                size="small"
                                placeholder={defaultColors.light.secondary}
                                disabled={saving}
                                sx={{ flexGrow: 1 }}
                            />
                        )}
                    </Box>

                    {showAllColors && (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                {langStrings.onPrimaryColor}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <TextField
                                    type="color"
                                    value={tempOnPrimaryColor}
                                    onChange={handleOnPrimaryColorChange}
                                    size="small"
                                    disabled={saving}
                                    sx={{ width: 70 }}
                                />
                                <TextField
                                    value={tempOnPrimaryColor}
                                    onChange={handleOnPrimaryColorChange}
                                    size="small"
                                    placeholder={defaultColors.light.onPrimary}
                                    disabled={saving}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                                {langStrings.onSecondaryColor}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <TextField
                                    type="color"
                                    value={tempOnSecondaryColor}
                                    onChange={handleOnSecondaryColorChange}
                                    size="small"
                                    disabled={saving}
                                    sx={{ width: 70 }}
                                />
                                <TextField
                                    value={tempOnSecondaryColor}
                                    onChange={handleOnSecondaryColorChange}
                                    size="small"
                                    placeholder={defaultColors.light.onSecondary}
                                    disabled={saving}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>
                        </>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={applyColorChanges}
                            fullWidth
                            sx={{ bgcolor: theme.palette.primary.main }}
                            disabled={!changed || saving}
                        >
                            {langStrings.save}
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={reset}
                            fullWidth
                            disabled={saving || isDefaultTheme(tempPrimaryColor, tempSecondaryColor)}
                        >
                            {langStrings.resetToDefaults}
                        </Button>
                    </Box>
                </Box>
            )}

            {fullWidth && !isMobile && <Divider orientation="vertical" flexItem />}
            {(!fullWidth || isMobile) && <Divider sx={{ my: 1.5 }} />}

            <Box
                sx={{
                    mt: isMobile || !fullWidth ? 2 : 0,
                    width: fullWidth && !isMobile ? '25%' : '100%',
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {langStrings.colorPreview}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Box
                        sx={{
                            height: 30,
                            flex: 1,
                            backgroundColor: tempPrimaryColor,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: tempOnPrimaryColor,
                            fontSize: '0.75rem',
                        }}
                    >
                        {langStrings.primaryColor}
                    </Box>
                    <Box
                        sx={{
                            height: 30,
                            flex: 1,
                            backgroundColor: tempSecondaryColor,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: tempOnSecondaryColor,
                            fontSize: '0.75rem',
                        }}
                    >
                        {langStrings.secondaryColor}
                    </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    Gradient Vorschau:
                </Typography>
                <Box
                    sx={{
                        height: fullWidth && !isMobile ? 100 : 50,
                        width: '100%',
                        mt: 0.5,
                        borderRadius: 1,
                        background:
                            isValidColor(tempPrimaryColor) && isValidColor(tempSecondaryColor)
                                ? isDarkMode
                                    ? `linear-gradient(to bottom right, ${alpha(tempPrimaryColor, 0.5)}, ${alpha(tempSecondaryColor, 0.5)})`
                                    : `linear-gradient(to bottom right, ${alpha(tempPrimaryColor, 0.5)}, ${alpha(tempSecondaryColor, 0.5)})`
                                : theme.palette.background.default,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                />

                {showSettingsLink && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <SettingsLink />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function SettingsLink() {
    const navigate = useNavigate();
    const theme = useTheme();

    const handleClick = () => {
        navigate(`/${ELinks.SETTINGS_STYLE}`);
    };

    return (
        <Button
            variant="text"
            size="small"
            startIcon={<Settings fontSize="small" />}
            onClick={handleClick}
            sx={{
                borderRadius: 4,
                textTransform: 'none',
                color: theme.palette.primary.main,
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
            }}
        >
            More Settings
        </Button>
    );
}
